import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchWorkflowById,
  fetchRoles,
  fetchUsers,
  fetchStatuses,
  fetchEventTypes,
  fetchGroups,
  createWorkflow,
  updateWorkflowAction,
  addNewApprovalLevel,
  removeNewApprovalLevel,
  setSelectedWorkflow,
  setValidationErrors,
  setApprovalLevelDialogOpen,
  setSelectedApprovalLevel,
  setEditingApprovalLevel,
  setApprovalLevelValidationErrors,
  clearNewApprovalLevels,
} from '../../store/reducers/workflowSlice';
import { CreateWorkflowRequest, UpdateWorkflowRequest, Role, ApprovalLevel } from '../../services/workflowApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  form: {
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(2),
    },
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(2),
    },
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
  },
  approvalLevelsSection: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  approvalLevelsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.25),
    },
  },
  approvalLevelGroup: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  approvalLevelGroupTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const WorkflowEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const isEditing = Boolean(id);
  const workflowId = id ? parseInt(id, 10) : null;

  const {
    selectedWorkflow,
    roles,
    users,
    statuses,
    eventTypes,
    groups,
    loading,
    error,
    validationErrors,
    isApprovalLevelDialogOpen,
    selectedApprovalLevel,
    isEditingApprovalLevel,
    approvalLevelValidationErrors,
    newApprovalLevels,
  } = useSelector((state: RootState) => state.workflow);

  // Form state
  const [formData, setFormData] = useState({
    ProcessName: '',
    Roles: [] as Role[],
    Active: true,
  });

  // Approval level form state - Now includes Events and Groups
  const [approvalLevelFormData, setApprovalLevelFormData] = useState({
    ApprovedById: 0,
    ApproveStatusId: 0,
    ApproveEventTypeId: 0,
    ApproveGroupId: 0,
    RejectStatusId: 0,
    RejectEventTypeId: 0,
    RejectGroupId: 0,
    PendingStatusId: 0,
    PendingEventTypeId: 0,
    PendingGroupId: 0,
  });

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchUsers());
    dispatch(fetchStatuses());
    dispatch(fetchEventTypes());
    dispatch(fetchGroups());
    
    if (isEditing && workflowId) {
      dispatch(fetchWorkflowById(workflowId));
    } else {
      dispatch(setSelectedWorkflow(null));
      dispatch(clearNewApprovalLevels());
    }

    // Clear validation errors
    dispatch(setValidationErrors([]));
  }, [dispatch, isEditing, workflowId]);

  // Update form data when selectedWorkflow changes
  useEffect(() => {
    if (isEditing && selectedWorkflow) {
      setFormData({
        ProcessName: selectedWorkflow.ProcessName || '',
        Roles: selectedWorkflow.Roles || [],
        Active: selectedWorkflow.Active ?? true,
      });
    } else if (!isEditing) {
      setFormData({
        ProcessName: '',
        Roles: [],
        Active: true,
      });
    }
  }, [selectedWorkflow, isEditing]);

  // Update approval level form when selectedApprovalLevel changes
  useEffect(() => {
    if (selectedApprovalLevel) {
      setApprovalLevelFormData({
        ApprovedById: selectedApprovalLevel.ApprovedById || 0,
        ApproveStatusId: selectedApprovalLevel.ApproveStatusId || 0,
        ApproveEventTypeId: selectedApprovalLevel.ApproveEventTypeId || 0,
        ApproveGroupId: selectedApprovalLevel.ApproveGroupId || 0,
        RejectStatusId: selectedApprovalLevel.RejectStatusId || 0,
        RejectEventTypeId: selectedApprovalLevel.RejectEventTypeId || 0,
        RejectGroupId: selectedApprovalLevel.RejectGroupId || 0,
        PendingStatusId: selectedApprovalLevel.PendingStatusId || 0,
        PendingEventTypeId: selectedApprovalLevel.PendingEventTypeId || 0,
        PendingGroupId: selectedApprovalLevel.PendingGroupId || 0,
      });
    } else {
      setApprovalLevelFormData({
        ApprovedById: 0,
        ApproveStatusId: 0,
        ApproveEventTypeId: 0,
        ApproveGroupId: 0,
        RejectStatusId: 0,
        RejectEventTypeId: 0,
        RejectGroupId: 0,
        PendingStatusId: 0,
        PendingEventTypeId: 0,
        PendingGroupId: 0,
      });
    }
  }, [selectedApprovalLevel]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      dispatch(setValidationErrors([]));
    }
  };

  const handleApprovalLevelInputChange = (field: string, value: any) => {
    setApprovalLevelFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation errors when user starts typing
    if (approvalLevelValidationErrors.length > 0) {
      dispatch(setApprovalLevelValidationErrors([]));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const approvalLevels = [
      ...(selectedWorkflow?.ApprovalLevels || []),
      ...newApprovalLevels.map((level, index) => ({
        ...level,
        Level: (selectedWorkflow?.ApprovalLevels?.length || 0) + index + 1
      }))
    ];

    if (isEditing && workflowId) {
      const updateData: UpdateWorkflowRequest = {
        Id: workflowId,
        ...formData,
        ApprovalLevels: approvalLevels,
      };
      dispatch(updateWorkflowAction(updateData));
    } else {
      const createData: CreateWorkflowRequest = {
        ...formData,
        ApprovalLevels: approvalLevels,
      };
      dispatch(createWorkflow(createData));
    }
  };

  const handleCancel = () => {
    navigate('/workflow');
  };

  const handleBack = () => {
    navigate('/workflow');
  };

  // Approval Level handlers
  const handleAddApprovalLevel = () => {
    dispatch(setSelectedApprovalLevel(null));
    dispatch(setEditingApprovalLevel(false));
    dispatch(setApprovalLevelDialogOpen(true));
  };

  const handleEditApprovalLevel = (level: ApprovalLevel, isNew: boolean = false) => {
    dispatch(setSelectedApprovalLevel(level));
    dispatch(setEditingApprovalLevel(true));
    dispatch(setApprovalLevelDialogOpen(true));
  };

  const handleDeleteApprovalLevel = (levelId: number | undefined, isNew: boolean = false) => {
    if (window.confirm('Are you sure you want to delete this approval level?')) {
      if (isNew) {
        // Find index in new approval levels array
        const index = newApprovalLevels.findIndex(level => 
          level.ApprovedById === levelId || 
          (level as any).tempId === levelId
        );
        if (index !== -1) {
          dispatch(removeNewApprovalLevel(index));
        }
      } else if (levelId) {
        // Handle existing approval level deletion
        // This would need to be implemented based on your backend API
        console.log('Delete existing approval level:', levelId);
      }
    }
  };

  const handleApprovalLevelSubmit = () => {
    const newLevel: ApprovalLevel = {
      Level: getAllApprovalLevels().length + 1,
      ...approvalLevelFormData,
      ApprovedBy: roles.find(r => r.Id === approvalLevelFormData.ApprovedById),
      ApproveStatus: statuses.find(s => s.Id === approvalLevelFormData.ApproveStatusId),
      ApproveEventType: eventTypes.find(e => e.Id === approvalLevelFormData.ApproveEventTypeId),
      ApproveGroup: groups.find(g => g.Id === approvalLevelFormData.ApproveGroupId),
      RejectStatus: statuses.find(s => s.Id === approvalLevelFormData.RejectStatusId),
      RejectEventType: eventTypes.find(e => e.Id === approvalLevelFormData.RejectEventTypeId),
      RejectGroup: groups.find(g => g.Id === approvalLevelFormData.RejectGroupId),
      PendingStatus: statuses.find(s => s.Id === approvalLevelFormData.PendingStatusId),
      PendingEventType: eventTypes.find(e => e.Id === approvalLevelFormData.PendingEventTypeId),
      PendingGroup: groups.find(g => g.Id === approvalLevelFormData.PendingGroupId),
      // Add temporary ID for new levels
      ...(!(selectedApprovalLevel?.Id) && { tempId: Date.now() })
    };

    if (isEditingApprovalLevel && selectedApprovalLevel?.Id) {
      // Update existing level logic would go here
      console.log('Update existing approval level');
    } else {
      // Add new level
      dispatch(addNewApprovalLevel(newLevel));
    }
    
    dispatch(setApprovalLevelDialogOpen(false));
  };

  const handleCloseApprovalLevelDialog = () => {
    dispatch(setApprovalLevelDialogOpen(false));
  };

  const getRoleById = (roleId: number) => {
    return roles.find(role => role.Id === roleId);
  };

  const getStatusById = (statusId: number) => {
    return statuses.find(status => status.Id === statusId);
  };

  const getEventTypeById = (eventTypeId: number) => {
    return eventTypes.find(eventType => eventType.Id === eventTypeId);
  };

  const getGroupById = (groupId: number) => {
    return groups.find(group => group.Id === groupId);
  };

  const getAllApprovalLevels = () => {
    return [
      ...(selectedWorkflow?.ApprovalLevels || []),
      ...newApprovalLevels
    ];
  };

  if (loading && isEditing && !selectedWorkflow) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper className={classes.paper}>
        {/* Header */}
        <Box className={classes.header}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBack}
            style={{ marginBottom: 16 }}
          >
            Back to Workflows
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit Workflow' : 'Add New Workflow'}
          </Typography>
          {isEditing && selectedWorkflow && (
            <Typography variant="subtitle1" color="textSecondary">
              Workflow: {selectedWorkflow.ProcessName}
            </Typography>
          )}
        </Box>

        <Divider style={{ marginBottom: 24 }} />

        {/* Form */}
        <form onSubmit={handleSubmit} className={classes.form}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Process Name"
                variant="outlined"
                value={formData.ProcessName}
                onChange={(e) => handleInputChange('ProcessName', e.target.value)}
                placeholder="Enter process name"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Roles (Creators)</InputLabel>
                <Select
                  multiple
                  value={formData.Roles.map(r => r.Id)}
                  onChange={(e) => {
                    const selectedRoleIds = e.target.value as number[];
                    const selectedRoles = roles.filter(role => selectedRoleIds.includes(role.Id));
                    handleInputChange('Roles', selectedRoles);
                  }}
                  label="Roles (Creators)"
                  renderValue={(selected) => (
                    <Box className={classes.chipContainer}>
                      {(selected as number[]).map((roleId) => {
                        const role = getRoleById(roleId);
                        return (
                          <Chip
                            key={roleId}
                            label={role?.Name || `Role ${roleId}`}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.Id} value={role.Id}>
                      {role.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active}
                    onChange={(e) => handleInputChange('Active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          {/* Button Group */}
          <Box className={classes.buttonGroup}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update Workflow' : 'Create Workflow'
              )}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </form>

        {/* Approval Levels Section */}
        <Box className={classes.approvalLevelsSection}>
          <Box className={classes.approvalLevelsHeader}>
            <Typography variant="h6">Approval Levels</Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddApprovalLevel}
            >
              Add Approval Level
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Level</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Approve Status</TableCell>
                  <TableCell>Reject Status</TableCell>
                  <TableCell>Pending Status</TableCell>
                  <TableCell>Events</TableCell>
                  <TableCell>Groups</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getAllApprovalLevels().length > 0 ? (
                  getAllApprovalLevels().map((level, index) => (
                    <TableRow key={level.Id || (level as any).tempId || index}>
                      <TableCell>{level.Level || index + 1}</TableCell>
                      <TableCell>
                        {level.ApprovedBy?.Name || 
                         getRoleById(level.ApprovedById)?.Name || 
                         'Unknown Role'}
                      </TableCell>
                      <TableCell>
                        {level.ApproveStatus?.StatusName || 
                         getStatusById(level.ApproveStatusId)?.StatusName || 
                         'Unknown Status'}
                      </TableCell>
                      <TableCell>
                        {level.RejectStatus?.StatusName || 
                         getStatusById(level.RejectStatusId)?.StatusName || 
                         'Unknown Status'}
                      </TableCell>
                      <TableCell>
                        {level.PendingStatus?.StatusName || 
                         getStatusById(level.PendingStatusId)?.StatusName || 
                         'Unknown Status'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          A: {getEventTypeById(level.ApproveEventTypeId || 0)?.Name || 'None'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          R: {getEventTypeById(level.RejectEventTypeId || 0)?.Name || 'None'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          P: {getEventTypeById(level.PendingEventTypeId || 0)?.Name || 'None'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          A: {getGroupById(level.ApproveGroupId || 0)?.Name || 'None'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          R: {getGroupById(level.RejectGroupId || 0)?.Name || 'None'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          P: {getGroupById(level.PendingGroupId || 0)?.Name || 'None'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEditApprovalLevel(level, !level.Id)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteApprovalLevel(
                            level.Id || (level as any).tempId, 
                            !level.Id
                          )}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No approval levels defined
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Enhanced Approval Level Dialog - Now matches Angular optisEditLevelModal */}
      <Dialog 
        open={isApprovalLevelDialogOpen} 
        onClose={handleCloseApprovalLevelDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isEditingApprovalLevel ? 'Edit Approval Level' : 'Add Approval Level'}
        </DialogTitle>
        <DialogContent>
          {approvalLevelValidationErrors.length > 0 && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {approvalLevelValidationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
          
          <Grid container spacing={3}>
            {/* Approved By Role */}
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Approved By (Role)</InputLabel>
                <Select
                  value={approvalLevelFormData.ApprovedById}
                  onChange={(e) => handleApprovalLevelInputChange('ApprovedById', e.target.value)}
                  label="Approved By (Role)"
                >
                  <MenuItem value={0}>
                    <em>Select Role</em>
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.Id} value={role.Id}>
                      {role.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Approval Configuration */}
            <Grid item xs={12}>
              <Box className={classes.approvalLevelGroup}>
                <Typography className={classes.approvalLevelGroupTitle}>
                  Approval Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Approve Status</InputLabel>
                      <Select
                        value={approvalLevelFormData.ApproveStatusId}
                        onChange={(e) => handleApprovalLevelInputChange('ApproveStatusId', e.target.value)}
                        label="Approve Status"
                      >
                        <MenuItem value={0}>
                          <em>Select Status</em>
                        </MenuItem>
                        {statuses.map((status) => (
                          <MenuItem key={status.Id} value={status.Id}>
                            {status.StatusName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Approve Event</InputLabel>
                      <Select
                        value={approvalLevelFormData.ApproveEventTypeId}
                        onChange={(e) => handleApprovalLevelInputChange('ApproveEventTypeId', e.target.value)}
                        label="Approve Event"
                      >
                        <MenuItem value={0}>
                          <em>Select Event</em>
                        </MenuItem>
                        {eventTypes.map((event) => (
                          <MenuItem key={event.Id} value={event.Id}>
                            {event.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Approve Group</InputLabel>
                      <Select
                        value={approvalLevelFormData.ApproveGroupId}
                        onChange={(e) => handleApprovalLevelInputChange('ApproveGroupId', e.target.value)}
                        label="Approve Group"
                      >
                        <MenuItem value={0}>
                          <em>Select Group</em>
                        </MenuItem>
                        {groups.map((group) => (
                          <MenuItem key={group.Id} value={group.Id}>
                            {group.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Rejection Configuration */}
            <Grid item xs={12}>
              <Box className={classes.approvalLevelGroup}>
                <Typography className={classes.approvalLevelGroupTitle}>
                  Rejection Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Reject Status</InputLabel>
                      <Select
                        value={approvalLevelFormData.RejectStatusId}
                        onChange={(e) => handleApprovalLevelInputChange('RejectStatusId', e.target.value)}
                        label="Reject Status"
                      >
                        <MenuItem value={0}>
                          <em>Select Status</em>
                        </MenuItem>
                        {statuses.map((status) => (
                          <MenuItem key={status.Id} value={status.Id}>
                            {status.StatusName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Reject Event</InputLabel>
                      <Select
                        value={approvalLevelFormData.RejectEventTypeId}
                        onChange={(e) => handleApprovalLevelInputChange('RejectEventTypeId', e.target.value)}
                        label="Reject Event"
                      >
                        <MenuItem value={0}>
                          <em>Select Event</em>
                        </MenuItem>
                        {eventTypes.map((event) => (
                          <MenuItem key={event.Id} value={event.Id}>
                            {event.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Reject Group</InputLabel>
                      <Select
                        value={approvalLevelFormData.RejectGroupId}
                        onChange={(e) => handleApprovalLevelInputChange('RejectGroupId', e.target.value)}
                        label="Reject Group"
                      >
                        <MenuItem value={0}>
                          <em>Select Group</em>
                        </MenuItem>
                        {groups.map((group) => (
                          <MenuItem key={group.Id} value={group.Id}>
                            {group.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Pending Configuration */}
            <Grid item xs={12}>
              <Box className={classes.approvalLevelGroup}>
                <Typography className={classes.approvalLevelGroupTitle}>
                  Pending Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Pending Status</InputLabel>
                      <Select
                        value={approvalLevelFormData.PendingStatusId}
                        onChange={(e) => handleApprovalLevelInputChange('PendingStatusId', e.target.value)}
                        label="Pending Status"
                      >
                        <MenuItem value={0}>
                          <em>Select Status</em>
                        </MenuItem>
                        {statuses.map((status) => (
                          <MenuItem key={status.Id} value={status.Id}>
                            {status.StatusName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Pending Event</InputLabel>
                      <Select
                        value={approvalLevelFormData.PendingEventTypeId}
                        onChange={(e) => handleApprovalLevelInputChange('PendingEventTypeId', e.target.value)}
                        label="Pending Event"
                      >
                        <MenuItem value={0}>
                          <em>Select Event</em>
                        </MenuItem>
                        {eventTypes.map((event) => (
                          <MenuItem key={event.Id} value={event.Id}>
                            {event.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Pending Group</InputLabel>
                      <Select
                        value={approvalLevelFormData.PendingGroupId}
                        onChange={(e) => handleApprovalLevelInputChange('PendingGroupId', e.target.value)}
                        label="Pending Group"
                      >
                        <MenuItem value={0}>
                          <em>Select Group</em>
                        </MenuItem>
                        {groups.map((group) => (
                          <MenuItem key={group.Id} value={group.Id}>
                            {group.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApprovalLevelDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleApprovalLevelSubmit}
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {isEditingApprovalLevel ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowEdit; 